import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { App, Empty, Image, Modal, Spin, Typography } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { getTopic } from '@/api/topics';
import { getLinkPreview, importPrototype } from '@/api/flyerDocs';
import { readLinksOfType } from '@/types/topic';
import type { ContentLink } from '@/types/topic';
import type { FlyerDocument } from '@/types/flyerDoc';

const RASTER_RE = /\.(png|jpe?g|webp)(\?.*)?$/i;
const PDF_RE = /\.pdf(\?.*)?$/i;

interface Props {
  open: boolean;
  docId: string | null;
  topicId: string | null;
  onClose: () => void;
  onImported: (doc: FlyerDocument) => void;
}

type ProtoLink = { url: string; name: string; kind: 'raster' | 'pdf' };

export function PrototypeImportModal({ open, docId, topicId, onClose, onImported }: Props) {
  const { message } = App.useApp();
  const [busyUrl, setBusyUrl] = useState<string | null>(null);

  const topicQ = useQuery({
    queryKey: ['topic', topicId, 'proto'],
    queryFn: () => getTopic(topicId!),
    enabled: open && !!topicId,
  });

  const links = useMemo<ProtoLink[]>(() => {
    const d = topicQ.data?.details;
    if (!d) return [];
    const all: ContentLink[] = [...readLinksOfType(d, 'flyer'), ...readLinksOfType(d, 'attachment')];
    const seen = new Set<string>();
    const out: ProtoLink[] = [];
    for (const l of all) {
      if (seen.has(l.url)) continue;
      seen.add(l.url);
      if (RASTER_RE.test(l.url)) out.push({ url: l.url, name: l.name || l.url, kind: 'raster' });
      else if (PDF_RE.test(l.url)) out.push({ url: l.url, name: l.name || l.url, kind: 'pdf' });
    }
    return out;
  }, [topicQ.data]);

  const pick = async (url: string) => {
    if (!docId || busyUrl) return;
    setBusyUrl(url);
    try {
      const doc = await importPrototype(docId, url);
      message.success('Prototype imported as background');
      onImported(doc);
      onClose();
    } catch (e) { message.error((e as Error).message); }
    finally { setBusyUrl(null); }
  };

  return (
    <Modal open={open} onCancel={() => !busyUrl && onClose()} footer={null} width={720}
      title="Import a prototype from this program's flyers">
      {!topicId ? <Empty description="Bind a program first" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        : topicQ.isLoading ? <Spin />
        : links.length === 0 ? <Empty description="This program has no flyer/attachment image or PDF links" />
        : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
            {links.map((l) => (
              <div key={l.url} style={{ cursor: busyUrl ? 'wait' : 'pointer', opacity: busyUrl && busyUrl !== l.url ? 0.5 : 1 }}
                onClick={() => pick(l.url)}>
                <div style={{ position: 'relative', height: 130, border: '1px solid #eee', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                  {l.kind === 'raster'
                    ? <img src={l.url} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    : <PdfThumb url={l.url} />}
                  {busyUrl === l.url && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,.6)' }}><Spin /></div>}
                </div>
                <Typography.Paragraph ellipsis={{ rows: 1 }} style={{ fontSize: 11, margin: '4px 0 0' }}>{l.name}</Typography.Paragraph>
              </div>
            ))}
          </div>
        )}
    </Modal>
  );
}

function PdfThumb({ url }: { url: string }) {
  const q = useQuery({ queryKey: ['link-preview', url], queryFn: () => getLinkPreview(url), staleTime: Infinity });
  if (q.isLoading) return <Spin />;
  if (q.isError || !q.data?.url) return <FilePdfOutlined style={{ fontSize: 40, color: '#cf1322' }} />;
  return <Image preview={false} src={q.data.url} style={{ maxWidth: '100%', maxHeight: 130, objectFit: 'contain' }} />;
}
